import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { reds } from '@/styles/colors';
import { formatChangedAt, statusLabel } from '../utils/staleSave';

const VARIANTS = {
    // Done was blocked because the order changed after the modal opened. Dismissable, and there is
    // deliberately no "Save anyway".
    stale: {
        title: 'This order changed after you opened it',
        body: (changedAt) => (
            <>
                {changedAt ? `It was updated at ${changedAt}. ` : 'It was updated. '}
                Saving now would overwrite those changes. Reload to get the latest version, then reapply your edits.
            </>
        ),
        noteLine: 'Your new note will be kept.',
        exitLabel: 'Keep editing',
    },
    // The live feed reported a change from someone else. Blocks the form: no backdrop or Escape
    // close, so the only ways out are Reload order or closing the edit form.
    live: {
        title: 'This order was updated',
        body: (changedAt) => (
            <>
                {changedAt ? `Updated at ${changedAt}. ` : ''}
                Reload to get the latest version, then reapply your edits. You can't save until you reload.
            </>
        ),
        noteLine: 'Reloading keeps your new note. Closing the edit form discards it.',
        exitLabel: 'Close edit form',
    },
};

/**
 * The "this order changed under you" prompt, in two variants. `row` is `{ last_modified_at,
 * order_status }` from the guard read (`'stale'`) or the feed (`'live'`); `yourStatus` is the modal's
 * copy. `'stale'` dismisses through `onKeepEditing`; `'live'` exits through `onCloseForm`.
 * `onReload` loads the latest order in both.
 */
const VersionConflictDialog = ({
    open,
    variant = 'stale',
    row,
    yourStatus,
    hasNote,
    reloading,
    error,
    onReload,
    onKeepEditing,
    onCloseForm,
}) => {
    const isLive = variant === 'live';
    const copy = VARIANTS[isLive ? 'live' : 'stale'];
    const changedAt = formatChangedAt(row?.last_modified_at);
    const statusDiffers = row?.order_status && row.order_status !== yourStatus;
    const onExit = isLive ? onCloseForm : onKeepEditing;

    return (
        <Dialog
            open={open}
            onClose={isLive || reloading ? undefined : onKeepEditing}
            disableEscapeKeyDown={isLive}
            maxWidth="xs"
            fullWidth
        >
            <DialogTitle>{copy.title}</DialogTitle>
            <DialogContent>
                <p>{copy.body(changedAt)}</p>
                {statusDiffers ? (
                    <p>
                        <strong>
                            Now: {statusLabel(row.order_status)} · Your copy: {statusLabel(yourStatus)}
                        </strong>
                    </p>
                ) : null}
                {hasNote ? <p>{copy.noteLine}</p> : null}
                {error ? (
                    <p role="alert" style={{ color: reds.primary }}>
                        {error}
                    </p>
                ) : null}
            </DialogContent>
            <DialogActions>
                <SecondaryButton disabled={reloading} onClick={onExit}>
                    {copy.exitLabel}
                </SecondaryButton>
                <PrimaryButton loading={reloading} disabled={reloading} onClick={onReload}>
                    Reload order
                </PrimaryButton>
            </DialogActions>
        </Dialog>
    );
};

export default VersionConflictDialog;
